<script setup lang="ts">
defineProps<{
  url: string;
}>();

const sectionRef = ref<HTMLElement>();
const { isVisible } = useReveal(sectionRef, { delay: 100 });
</script>

<template>
  <section
    ref="sectionRef"
    class="reveal py-12 md:py-20"
    :class="{ visible: isVisible }"
  >
    <Background>
      <div class="max-w-6xl mx-auto px-6 py-16">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
          <!-- Left column -->
          <div>
            <p class="text-sm font-bold uppercase tracking-widest text-[var(--color-peach)]">
              Følg os på Facebook
            </p>
            <h2 class="mt-3 text-3xl md:text-4xl font-bold text-[var(--color-charcoal)]">
              Se når vores elever får kørekortet
            </h2>
            <p class="mt-6 text-[var(--color-charcoal)]/70">
              Succeshistorier, tips til køreprøven og daglige opdateringer — alt det deler vi på Facebook.
            </p>
            <div class="mt-8">
              <UButton
                :to="url"
                target="_blank"
                rel="noopener noreferrer"
                label="Følg os på Facebook"
                icon="i-simple-icons-facebook"
                variant="solid"
                color="primary"
                size="lg"
                class="rounded-full px-8"
              />
            </div>
          </div>

          <!-- Right column: decorative post cards -->
          <div class="relative h-72 md:h-80">
            <!-- Card 1: back -->
            <div class="post-card absolute left-8 top-8 w-56 md:w-64 bg-white rounded-xl shadow-lg overflow-hidden rotate-[-6deg]">
              <div class="h-2 bg-gradient-to-r from-[var(--color-peach)] to-[var(--color-terracotta)]" />
              <div class="p-4">
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-8 h-8 rounded-full bg-primary-100" />
                  <div>
                    <div class="w-20 h-2.5 bg-neutral-200 rounded" />
                    <div class="w-12 h-2 bg-neutral-100 rounded mt-1" />
                  </div>
                </div>
                <div class="space-y-2">
                  <div class="w-full h-2 bg-neutral-100 rounded" />
                  <div class="w-4/5 h-2 bg-neutral-100 rounded" />
                  <div class="w-3/4 h-2 bg-neutral-100 rounded" />
                </div>
              </div>
            </div>

            <!-- Card 2: middle -->
            <div class="post-card absolute left-16 top-4 w-56 md:w-64 bg-white rounded-xl shadow-lg overflow-hidden rotate-[3deg]">
              <div class="h-2 bg-gradient-to-r from-[var(--color-peach)] to-[var(--color-terracotta)]" />
              <div class="p-4">
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-8 h-8 rounded-full bg-[var(--color-peach)]/40" />
                  <div>
                    <div class="w-24 h-2.5 bg-neutral-200 rounded" />
                    <div class="w-14 h-2 bg-neutral-100 rounded mt-1" />
                  </div>
                </div>
                <div class="w-full h-20 bg-neutral-50 rounded-lg mb-3" />
                <div class="space-y-2">
                  <div class="w-full h-2 bg-neutral-100 rounded" />
                  <div class="w-2/3 h-2 bg-neutral-100 rounded" />
                </div>
              </div>
            </div>

            <!-- Card 3: front -->
            <div class="post-card absolute left-4 top-12 w-56 md:w-64 bg-white rounded-xl shadow-lg overflow-hidden rotate-[-2deg]">
              <div class="h-2 bg-gradient-to-r from-[var(--color-peach)] to-[var(--color-terracotta)]" />
              <div class="p-4">
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-8 h-8 rounded-full bg-[var(--color-terracotta)]/30" />
                  <div>
                    <div class="w-16 h-2.5 bg-neutral-200 rounded" />
                    <div class="w-10 h-2 bg-neutral-100 rounded mt-1" />
                  </div>
                </div>
                <div class="space-y-2">
                  <div class="w-full h-2 bg-neutral-100 rounded" />
                  <div class="w-5/6 h-2 bg-neutral-100 rounded" />
                  <div class="w-full h-2 bg-neutral-100 rounded" />
                  <div class="w-1/2 h-2 bg-neutral-100 rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Background>
  </section>
</template>

<style scoped>
.reveal .post-card {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.reveal.visible .post-card:nth-child(1) {
  opacity: 1;
  transform: translateY(0) rotate(-6deg);
  transition-delay: 0ms;
}

.reveal.visible .post-card:nth-child(2) {
  opacity: 1;
  transform: translateY(0) rotate(3deg);
  transition-delay: 150ms;
}

.reveal.visible .post-card:nth-child(3) {
  opacity: 1;
  transform: translateY(0) rotate(-2deg);
  transition-delay: 300ms;
}
</style>
